import React, { useLayoutEffect, useRef, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useAppSelector } from "../../app/hooks";
import { isAdminRole, UserRole } from "../../app/userSlice";
import Avatar from "../../components/Avatar";
import { useMessagesPageQuery, usePostMessageMutation } from "./messagesApiSlice";

export interface Props {
	className?: string;
	disabled?: boolean;
	draftId: number;
}
export default function Messages(props: Props) {
	const { role } = useAppSelector((state) => state.user);
	const result = useMessagesPageQuery(isAdminRole(role) ? skipToken : props.draftId, {
		refetchOnMountOrArgChange: true,
	});
	const data = result.data;

	if (!data || !data.showMessagesSection) return null;

	return (
		<section id="MessagesSection" className={props.className}>
			<h1>Messages</h1>
			<ul>
				{data.messages.map((message, index) => {
					const direction = message.isMessageFromCurrentUser
						? "flex-row-reverse"
						: "flex-row";
					const avatarpos = message.isMessageFromCurrentUser ? "ml-4" : "mr-4";
					const textpos = message.isMessageFromCurrentUser
						? "text-right pr-2"
						: "text-left pl-2";
					const messagepos = message.isMessageFromCurrentUser ? "pl-10" : "pr-10";
					const shape = message.isMessageFromCurrentUser
						? "rounded-l-3xl rounded-r-2xl"
						: "rounded-r-3xl rounded-l-2xl";
					const triangle = message.isMessageFromCurrentUser
						? "origin-top-left -rotate-[60deg] transform -mr-2 float-right"
						: "origin-top-right rotate-[60deg] transform -ml-2";
					const color = message.isMessageFromCurrentUser
						? "bg-primary-600/50"
						: "bg-primary-700/15";
					return (
						<li key={index} className={`${direction} flex items-start`}>
							<div className={`${avatarpos} pt-4 mt-1`}>
								<Avatar
									hasImage={message.senderHasImage}
									initials={message.senderInitials ?? "?"}
									userId={message.senderId}
								/>
							</div>
							<div className={`${messagepos} ${avatarpos}`}>
								<div className={`${textpos} text-sm text-gray-500`}>
									{message.name} - {message.dateCreated}
								</div>
								<div>
									<p
										className={`${color} ${shape} px-4 py-2 whitespace-pre-wrap message-bubble`}>
										{message.text && message.text.trim() !== ""
											? message.text
											: "\u00A0"}
									</p>
									<div className="overflow-hidden">
										<div
											className={`h-4 w-4 message-bubble ${triangle} ${color} ${messagepos}`}></div>
									</div>
								</div>
							</div>
						</li>
					);
				})}
			</ul>
			<PostMessage
				className="print:hidden mt-2"
				disabled={props.disabled}
				draftId={props.draftId}
				showSection={data.showPostMessageSection}
			/>
		</section>
	);
}

export interface _Props {
	className?: string;
	disabled?: boolean;
	draftId: number;
	showSection: boolean;
}

export function PostMessage(props: _Props) {
	const { role } = useAppSelector((state) => state.user);
	const characterLimit = 950;
	const minHeight = 32;
	const [message, setMessage] = useState<string>("");
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const [postMessage, postMessageResult] = usePostMessageMutation();

	useLayoutEffect(() => {
		if (!textareaRef.current) return;
		textareaRef.current.style.height = "inherit";
		textareaRef.current.style.height = `${Math.max(
			textareaRef.current.scrollHeight + 3,
			minHeight
		)}px`;
	}, [message]);

	function handleChangeEvent(event: React.FormEvent<HTMLTextAreaElement>) {
		let value = event.currentTarget.value;
		if (value.length > characterLimit) {
			alert(`Message is limited to ${characterLimit} characters.`);
			value = value.substring(0, characterLimit);
		}
		setMessage(value);
	}

	function handlePostMessage(event: React.FormEvent<HTMLButtonElement>) {
		const draftId = props.draftId;
		postMessage({ draftId, message });
		setMessage("");
	}

	if (!props.showSection) return null;

	return (
		<div className={props.className}>
			<label htmlFor="message-text" className="block font-bold">
				Send your {role === UserRole.Proctor ? "student" : "teacher"} a question or comment.
			</label>
			<textarea
				id="message-text"
				name="message"
				ref={textareaRef}
				className="block resize-none w-full min-h-[36px] rounded-xl"
				value={message}
				onChange={handleChangeEvent}></textarea>
			<button
				id="post-message-button"
				className="btn btn-primary block mt-2"
				onClick={handlePostMessage}
				disabled={props.disabled}>
				Send Message
			</button>
		</div>
	);
}
